import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Facebook, Instagram, Star } from "lucide-react";
import { SITE, SERVICES } from "../lib/site";

export default function Footer() {
    return (
        <footer
            className="bg-primary text-primary-foreground mt-24"
            data-testid="site-footer"
        >
            <div className="container-x py-16 md:py-20 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-4 space-y-4">
                    <div className="flex items-center gap-3">
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL}/api/uploads/dandy-logo.png`}
                            alt="Dandy Builders logo"
                            className="h-12 w-12 rounded-md object-contain bg-black p-1"
                        />
                        <div className="leading-tight">
                            <div className="font-heading font-bold text-lg">
                                {SITE.name}
                            </div>
                            <div className="text-xs uppercase tracking-[0.2em] opacity-70">
                                & Property Care
                            </div>
                        </div>
                    </div>
                    <p className="text-sm leading-relaxed opacity-80 max-w-sm">
                        Your friendly local builder in Wokingham, Berkshire.
                        Extensions, loft conversions, groundworks, electrical
                        and mechanical — built properly, since {SITE.foundedYear}.
                    </p>

                    {/* Google Reviews CTA */}
                    <a
                        href={SITE.googleReviewsUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="footer-google-reviews"
                        className="mt-4 inline-flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/15 hover:border-secondary/50 rounded-lg px-4 py-3 transition-colors group"
                    >
                        <span className="h-9 w-9 shrink-0 rounded-full bg-white grid place-items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                                <path fill="#FFC107" d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"/>
                                <path fill="#FF3D00" d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"/>
                                <path fill="#4CAF50" d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"/>
                                <path fill="#1976D2" d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"/>
                            </svg>
                        </span>
                        <span className="leading-tight">
                            <span className="block text-[10px] uppercase tracking-[0.2em] opacity-70">
                                Featured on Google
                            </span>
                            <span className="mt-0.5 flex items-center gap-1.5">
                                <span className="flex items-center gap-0.5 text-amber-400">
                                    {Array.from({ length: 5 }).map((_, i) => (
                                        <Star key={i} size={11} fill="currentColor" strokeWidth={0} />
                                    ))}
                                </span>
                                <span className="text-sm font-medium group-hover:text-secondary transition-colors">
                                    Rate us &amp; read reviews
                                </span>
                            </span>
                        </span>
                    </a>

                    <div className="flex gap-3 pt-3">
                        <a
                            href={SITE.facebook}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-facebook"
                            className="h-10 w-10 grid place-items-center rounded-md bg-white/10 hover:bg-secondary transition-colors"
                            aria-label="Facebook"
                        >
                            <Facebook size={18} strokeWidth={1.5} />
                        </a>
                        <a
                            href={SITE.instagram}
                            target="_blank"
                            rel="noreferrer"
                            data-testid="footer-instagram"
                            className="h-10 w-10 grid place-items-center rounded-md bg-white/10 hover:bg-secondary transition-colors"
                            aria-label="Instagram"
                        >
                            <Instagram size={18} strokeWidth={1.5} />
                        </a>
                    </div>
                </div>

                <div className="md:col-span-3">
                    <h4 className="text-sm uppercase tracking-[0.2em] opacity-70 mb-5">
                        Services
                    </h4>
                    <ul className="space-y-2">
                        {SERVICES.slice(0, 6).map((s) => (
                            <li key={s.slug}>
                                <Link
                                    to={`/services#${s.slug}`}
                                    className="text-sm opacity-90 hover:opacity-100 hover:text-secondary transition-colors"
                                    data-testid={`footer-service-${s.slug}`}
                                >
                                    {s.name}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <h4 className="text-sm uppercase tracking-[0.2em] opacity-70 mb-5">
                        Company
                    </h4>
                    <ul className="space-y-2 text-sm">
                        <li>
                            <Link to="/about" className="opacity-90 hover:opacity-100 hover:text-secondary transition-colors">About</Link>
                        </li>
                        <li>
                            <Link to="/projects" className="opacity-90 hover:opacity-100 hover:text-secondary transition-colors">Projects</Link>
                        </li>
                        <li>
                            <Link to="/faq" className="opacity-90 hover:opacity-100 hover:text-secondary transition-colors">FAQ</Link>
                        </li>
                        <li>
                            <Link to="/contact" className="opacity-90 hover:opacity-100 hover:text-secondary transition-colors">Contact</Link>
                        </li>
                        <li>
                            <Link to="/quote" className="opacity-90 hover:opacity-100 hover:text-secondary transition-colors">Get a Quote</Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-3">
                    <h4 className="text-sm uppercase tracking-[0.2em] opacity-70 mb-5">
                        Contact
                    </h4>
                    <ul className="space-y-3 text-sm">
                        <li className="flex gap-3 items-start">
                            <Phone size={16} className="mt-0.5 shrink-0 opacity-80" strokeWidth={1.5} />
                            <a href={SITE.phoneLink} className="hover:text-secondary" data-testid="footer-phone">{SITE.phone}</a>
                        </li>
                        <li className="flex gap-3 items-start">
                            <Mail size={16} className="mt-0.5 shrink-0 opacity-80" strokeWidth={1.5} />
                            <a href={SITE.emailLink} className="hover:text-secondary break-all" data-testid="footer-email">{SITE.email}</a>
                        </li>
                        <li className="flex gap-3 items-start">
                            <MapPin size={16} className="mt-0.5 shrink-0 opacity-80" strokeWidth={1.5} />
                            <span className="opacity-90">{SITE.location}</span>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="container-x py-6 flex flex-col md:flex-row gap-3 justify-between text-xs opacity-70">
                    <p>© {new Date().getFullYear()} {SITE.fullName}. All rights reserved.</p>
                    <p>Wokingham, Berkshire · UK · Fully insured</p>
                </div>
            </div>
        </footer>
    );
}
